import { useTranslation } from 'react-i18next';
import { retrieveFromSessionStorage } from '../util/helpers/sessionStorageHelper';
import { APPLICATION_TYPE } from '../constants/sessionStorage';
import {
  APPLICANT_TYPES,
  APPLICATION_TYPE_STRING,
  KYC_PERSON_TARGET_TYPES,
  PrimaryTargetType,
  BothTargetType,
  SecondaryTargetType,
  KYC_DOCUMENT_TYPES,
  Passport,
  DriverLicenseOrPassport,
  DriverLicenseOrStateIssuedIDCard,
  UtilityBill,
  BankStatement,
  SocialSecurityOrMedicareCard,
  VoidedCheck,
  Other,
} from '../util/constants';

import { useApplicantData } from './useApplicantData';
import { getApplicantData } from '../util/helpers/applicantDataHelper';
import { PAGES } from '../constants/pages';
import IdCardIcon from '../components/Icons/IdCardIcon';
import HouseIcon from '../components/Icons/HouseIcon';
import BuildingIcon from '../components/Icons/BuildingIcon';
import CheckIcon from '../components/Icons/CheckIcon';

const useKycVerification = (requiredDocuments) => {
  const { t } = useTranslation();
  const ApplicationType = retrieveFromSessionStorage(APPLICATION_TYPE);
  const MainApplicantData = useApplicantData(APPLICANT_TYPES.MAIN);
  const SecondaryApplicantData = useApplicantData(
    ApplicationType === APPLICATION_TYPE_STRING[5]
      ? APPLICANT_TYPES.MINOR
      : APPLICANT_TYPES.SECONDARY,
  );

  const proofOfIdentity = (isAllDocumentsUploaded) => ({
    title: t('kycVerification.proofOfIdentity'),
    Icon: isAllDocumentsUploaded ? CheckIcon : IdCardIcon,
    IsUploaded: isAllDocumentsUploaded,
    path: PAGES.KYC_PROOF_OF_IDENTITY.MAIN.MAIN,
    dataCy: 'button-proof-primary-holder',
  });

  const proofOfResidency = (isAllDocumentsUploaded) => ({
    title: t('kycVerification.proofOfResidency'),
    Icon: isAllDocumentsUploaded ? CheckIcon : HouseIcon,
    IsUploaded: isAllDocumentsUploaded,
    path: PAGES.KYC_PROOF_OF_RESIDENCE.MAIN.MAIN,
  });

  const proofOfOwnership = (isAllDocumentsUploaded, type) => ({
    title: t('kycVerification.proofOfOwnership'),
    Icon: isAllDocumentsUploaded ? CheckIcon : BuildingIcon,
    IsUploaded: isAllDocumentsUploaded,
    type,
    path: PAGES.KYC_PROOF_OF_OWNERSHIP.UPLOAD,
    dataCy: 'button-proof-secondary-holder',
  });
  const proofOfIdentityWithName = (
    name,
    path,
    isAllDocumentsUploaded,
    type,
  ) => ({
    title: t('kycVerification.proofOfIdentityNamed', { name }),
    Icon: isAllDocumentsUploaded ? CheckIcon : IdCardIcon,
    IsUploaded: isAllDocumentsUploaded,
    path,
    dataCy:
      type === 'Primary'
        ? 'button-proof-primary-holder'
        : 'button-proof-secondary-holder',
  });

  const proofOfResidencyWithName = (name, path, isAllDocumentsUploaded) => ({
    title: t('kycVerification.proofOfResidencyNamed', { name }),
    Icon: isAllDocumentsUploaded ? CheckIcon : HouseIcon,
    IsUploaded: isAllDocumentsUploaded,
    path,
  });

  const proofOfOwnershipWithName = (name, isAllDocumentsUploaded, type) => ({
    title: t('kycVerification.proofOfOwnershipNamed', { name }),
    Icon: isAllDocumentsUploaded ? CheckIcon : BuildingIcon,
    IsUploaded: isAllDocumentsUploaded,
    type,
    path: PAGES.KYC_PROOF_OF_OWNERSHIP.UPLOAD,
  });

  const getFilterDocuments = (type) => {
    if (!requiredDocuments.length) return;
    if (type === 'Primary') {
      return requiredDocuments.filter(
        (document) =>
          KYC_PERSON_TARGET_TYPES[document.PersonTargetType] ===
            PrimaryTargetType ||
          KYC_PERSON_TARGET_TYPES[document.PersonTargetType] === BothTargetType,
      );
    }
    return requiredDocuments.filter(
      (document) =>
        KYC_PERSON_TARGET_TYPES[document.PersonTargetType] ===
          SecondaryTargetType ||
        KYC_PERSON_TARGET_TYPES[document.PersonTargetType] === BothTargetType,
    );
  };

  const checkProofOfIdentity = (documentList) => {
    const proofOfIdentityList = documentList.filter(
      (document) =>
        KYC_DOCUMENT_TYPES[`${document.DocumentType}`] === Passport ||
        KYC_DOCUMENT_TYPES[`${document.DocumentType}`] ===
          DriverLicenseOrPassport ||
        KYC_DOCUMENT_TYPES[`${document.DocumentType}`] ===
          DriverLicenseOrStateIssuedIDCard,
    );
    const isAllIdentityDocumentsUploaded =
      proofOfIdentityList.filter((document) => document.IsUploaded).length ===
      proofOfIdentityList.length;
    const isProofOfIdentity = !!proofOfIdentityList.length;

    return { isProofOfIdentity, isAllIdentityDocumentsUploaded };
  };

  const checkProofOfResidency = (documentList) => {
    const proofOfResidencyList = documentList.filter(
      (document) =>
        KYC_DOCUMENT_TYPES[`${document.DocumentType}`] === UtilityBill ||
        KYC_DOCUMENT_TYPES[`${document.DocumentType}`] === BankStatement ||
        KYC_DOCUMENT_TYPES[`${document.DocumentType}`] === VoidedCheck ||
        KYC_DOCUMENT_TYPES[`${document.DocumentType}`] ===
          SocialSecurityOrMedicareCard,
    );
    const isAllResidencyDocumentsUploaded =
      proofOfResidencyList.filter((document) => document.IsUploaded).length ===
      proofOfResidencyList.length;
    const isProofOfResidency = !!proofOfResidencyList.length;

    return { isProofOfResidency, isAllResidencyDocumentsUploaded };
  };
  const checkProofOfOwnership = (documentList) => {
    const proofOfOwnershipList = documentList.filter(
      (document) => KYC_DOCUMENT_TYPES[`${document.DocumentType}`] === Other,
    );
    const isAllOwnershipDocumentsUploaded =
      proofOfOwnershipList.filter((document) => document.IsUploaded).length ===
      proofOfOwnershipList.length;
    const isProofOfOwnership = !!proofOfOwnershipList.length;

    return { isProofOfOwnership, isAllOwnershipDocumentsUploaded };
  };

  const getProofOfIdentity = (type, isAllDocumentsUploaded) => {
    if (!ApplicationType) {
      return [];
    }
    switch (ApplicationType) {
      case APPLICATION_TYPE_STRING[1]:
      case APPLICATION_TYPE_STRING[2]:
        return proofOfIdentity(isAllDocumentsUploaded);

      case APPLICATION_TYPE_STRING[4]:
      case APPLICATION_TYPE_STRING[5]:
        return type === 'Primary'
          ? proofOfIdentityWithName(
              getApplicantData(MainApplicantData, 'PersonName', 'FirstName'),
              PAGES.KYC_PROOF_OF_IDENTITY.MAIN.MAIN,
              isAllDocumentsUploaded,
              'Primary',
            )
          : proofOfIdentityWithName(
              getApplicantData(
                SecondaryApplicantData,
                'PersonName',
                'FirstName',
              ),
              ApplicationType === APPLICATION_TYPE_STRING[5]
                ? PAGES.KYC_PROOF_OF_IDENTITY.MINOR.MAIN
                : PAGES.KYC_PROOF_OF_IDENTITY.SECONDARY.MAIN,
              isAllDocumentsUploaded,
              'Secondary',
            );
      default:
        break;
    }
  };

  const getProofOfResidency = (type, isAllDocumentsUploaded) => {
    if (!ApplicationType) {
      return [];
    }
    switch (ApplicationType) {
      case APPLICATION_TYPE_STRING[1]:
      case APPLICATION_TYPE_STRING[2]:
        return proofOfResidency(isAllDocumentsUploaded);
      case APPLICATION_TYPE_STRING[4]:
      case APPLICATION_TYPE_STRING[5]:
        return type === 'Primary'
          ? proofOfResidencyWithName(
              getApplicantData(MainApplicantData, 'PersonName', 'FirstName'),
              PAGES.KYC_PROOF_OF_RESIDENCE.MAIN.MAIN,
              isAllDocumentsUploaded,
            )
          : proofOfResidencyWithName(
              getApplicantData(
                SecondaryApplicantData,
                'PersonName',
                'FirstName',
              ),
              ApplicationType === APPLICATION_TYPE_STRING[5]
                ? PAGES.KYC_PROOF_OF_RESIDENCE.MINOR.MAIN
                : PAGES.KYC_PROOF_OF_RESIDENCE.SECONDARY.MAIN,
              isAllDocumentsUploaded,
            );

      default:
        break;
    }
  };

  const getProofOfOwnership = (type, isAllDocumentsUploaded) => {
    if (!ApplicationType) {
      return [];
    }
    switch (ApplicationType) {
      case APPLICATION_TYPE_STRING[1]:
      case APPLICATION_TYPE_STRING[2]:
        return proofOfOwnership(isAllDocumentsUploaded, type);
      case APPLICATION_TYPE_STRING[4]:
      case APPLICATION_TYPE_STRING[5]:
        return type === 'Primary'
          ? proofOfOwnershipWithName(
              getApplicantData(MainApplicantData, 'PersonName', 'FirstName'),
              isAllDocumentsUploaded,
              type,
            )
          : proofOfOwnershipWithName(
              getApplicantData(
                SecondaryApplicantData,
                'PersonName',
                'FirstName',
              ),
              isAllDocumentsUploaded,
              type,
            );

      default:
        break;
    }
  };

  const filterOptionsBasedOnDocuments = (type) => {
    if (!requiredDocuments.length) return;

    const documents = getFilterDocuments(type);
    const {
      isProofOfIdentity,
      isAllIdentityDocumentsUploaded,
    } = checkProofOfIdentity(documents);
    const {
      isProofOfResidency,
      isAllResidencyDocumentsUploaded,
    } = checkProofOfResidency(documents);
    const {
      isProofOfOwnership,
      isAllOwnershipDocumentsUploaded,
    } = checkProofOfOwnership(documents);
    let list = [];

    if (isProofOfIdentity) {
      const proofOfIdentity = getProofOfIdentity(
        type,
        isAllIdentityDocumentsUploaded,
      );
      list = [...list, proofOfIdentity];
    }

    if (isProofOfResidency) {
      const proofOfResidency = getProofOfResidency(
        type,
        isAllResidencyDocumentsUploaded,
      );
      list = [...list, proofOfResidency];
    }

    if (isProofOfOwnership) {
      const proofOfOwnership = getProofOfOwnership(
        type,
        isAllOwnershipDocumentsUploaded,
      );
      list = [...list, proofOfOwnership];
    }
    return list;
  };

  const kycOptions = () => {
    if (!ApplicationType) {
      return [];
    }
    const primaryList = filterOptionsBasedOnDocuments('Primary');
    const secondaryList = filterOptionsBasedOnDocuments('Secondary');

    if (!primaryList && !secondaryList) return;

    return [...primaryList, ...secondaryList];
  };
  return { kycOptions };
};

useKycVerification.propTypes = {};

export default useKycVerification;
